// src/components/TZMMS-INV/utils/inventoryList.js
// ============================================================================
// TZMMS Inventory Counter — pure inventory / email helper functions
// ----------------------------------------------------------------------------
// PURPOSE
// - Keep non-React formatting and entry-building logic out of components.
// - Provide small, reusable helpers for scan normalization and email creation.
// ============================================================================

import { EMAIL_SUBJECT_PREFIX } from '../constants/tzmmsInvUi.js';

// ============================================================================
// BLOCK TZI.UTIL.001 — Safe string helpers
// ============================================================================
export function trimStr(value) {
  return typeof value === 'string' ? value.trim() : String(value ?? '').trim();
}

export function normalizeScannedText(rawValue) {
  return trimStr(rawValue)
    .replace(/\r\n/g, '\n') // Normalize Windows newlines.
    .replace(/\r/g, '\n') // Normalize legacy carriage returns.
    .trim(); // Final trim prevents blank rows from whitespace-only QR values.
}

// ============================================================================
// BLOCK TZI.UTIL.002 — Time formatting helpers
// ============================================================================
export function formatDisplayDateTime(input) {
  const date = input instanceof Date ? input : new Date(input);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
}

function createEntryId() {
  if (globalThis?.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID(); // Browser-native UUID when available.
  }

  return `tzi-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ============================================================================
// BLOCK TZI.UTIL.003 — Inventory entry builder
// ============================================================================
export function buildInventoryEntry(rawValue, scannedAt = new Date()) {
  const scannedDate = scannedAt instanceof Date ? scannedAt : new Date(scannedAt);
  const normalizedValue = normalizeScannedText(rawValue);

  return {
    id: createEntryId(), // Stable key for React rendering + row selection.
    rawValue: normalizedValue, // Exact value that will be shown and emailed.
    scannedAtIso: scannedDate.toISOString(), // Machine-friendly timestamp.
    scannedAtDisplay: formatDisplayDateTime(scannedDate), // User-friendly timestamp.
  };
}

// ============================================================================
// BLOCK TZI.UTIL.004 — Email content builders
// ============================================================================
export function buildInventoryEmailSubject(entries, generatedAt = new Date()) {
  const count = Array.isArray(entries) ? entries.length : 0;
  const generatedText = formatDisplayDateTime(generatedAt);

  return `${EMAIL_SUBJECT_PREFIX} — ${count} Item${count === 1 ? '' : 's'} — ${generatedText}`;
}

export function buildInventoryEmailMessage(entries, generatedAt = new Date(), note = '') {
  const safeEntries = Array.isArray(entries) ? entries : [];
  const lines = safeEntries.map((entry) => trimStr(entry?.rawValue)).filter(Boolean);
  const safeNote = trimStr(note);

  // Prepend note block above scan list when the user entered one.
  const notePart = safeNote ? [`Note:\n${safeNote}`, ''] : [];

  return [...notePart, 'Scanned Values:', ...lines].join('\n');
}
